/**
 * BrandingStudio — editor de marca del organizador con preview vivo.
 *
 * Layout split-panel (basado en el mockup de Claude Design):
 *   - Izquierda 452px: formulario (logo, portada, color, textos)
 *   - Derecha flex-1: preview real con toggle Landing / Panel / Taquilla
 *
 * Cada toggle muestra cómo se verá el brand en ese contexto exacto:
 *   - Landing (dramático editorial, --brand mucho)
 *   - Panel (sobrio profesional, --brand solo acompaña)
 *   - Taquilla (dark utility, --brand solo en logo/CTA, verde/rojo intocables)
 *
 * El preview usa var(--brand) via un scope local — cambia el color y
 * las 3 pantallas se actualizan al instante sin re-renderizar la app.
 *
 * Props:
 *   entity: valores actuales (name, logo_url, cover_url, accent_color, etc.)
 *   onSave: callback al guardar, recibe el payload parcial
 *   busy: bloquea el botón durante el guardado
 */
import { useMemo, useState } from 'react';
import {
  buildBrandTokens, contrastRatio, readableTextOn,
  validateBrandColor, withAlpha,
} from '../utils/color';

interface BrandingValues {
  name: string;
  description: string;
  logo_url: string;
  cover_url: string;
  accent_color: string;
  website_url: string;
  instagram_url: string;
}

interface Props {
  entity: Partial<BrandingValues> & { slug: string };
  onSave: (values: BrandingValues) => void | Promise<void>;
  busy?: boolean;
}

type World = 'landing' | 'panel' | 'taquilla';

const MAX_SHORT = 20;
const MAX_LONG = 80;
const MAX_DESC = 400;

export default function BrandingStudio({ entity, onSave, busy = false }: Props) {
  const [form, setForm] = useState<BrandingValues>({
    name: entity.name || '',
    description: entity.description || '',
    logo_url: entity.logo_url || '',
    cover_url: entity.cover_url || '',
    accent_color: entity.accent_color || '#1E2A2E',
    website_url: entity.website_url || '',
    instagram_url: entity.instagram_url || '',
  });
  const [world, setWorld] = useState<World>('landing');

  // — Validación de color en vivo —
  const validation = form.accent_color ? validateBrandColor(form.accent_color) : { ok: true as const };
  const isColorOk = validation.ok;
  const tokens = useMemo(() => buildBrandTokens(isColorOk ? form.accent_color : null), [form.accent_color, isColorOk]);
  const onWhitePass = contrastRatio(form.accent_color || '#1E2A2E', '#FFFFFF') >= 3;
  const onBlackPass = contrastRatio(form.accent_color || '#1E2A2E', '#0A0A0A') >= 3;

  const shortName = form.name.slice(0, MAX_SHORT);
  const monogram = form.name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '·';

  const patch = (k: keyof BrandingValues, v: string) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!isColorOk || busy) return;
    await onSave(form);
  };

  const scopeStyle: React.CSSProperties = {
    ['--brand' as string]: tokens.brand,
    ['--brand-ink' as string]: tokens.brandInk,
    ['--brand-soft' as string]: tokens.brandSoft,
    ['--brand-strong' as string]: tokens.brandStrong,
    ['--brand-border' as string]: tokens.brandBorder,
  };

  return (
    <div style={{ display: 'flex', minHeight: 720, background: '#E9E4DA', borderRadius: 12, overflow: 'hidden', ...scopeStyle }}>
      {/* ═══ FORMULARIO IZQUIERDA ═══ */}
      <aside style={{ width: 452, flex: '0 0 452px', background: '#FBF7F0', borderRight: '1px solid rgba(30,42,46,.1)', overflowY: 'auto', maxHeight: '80vh' }}>
        <div style={{ padding: '28px 30px 40px' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--brand)' }} />
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: '#7d8a8d' }}>
              Pagafácil · Branding
            </span>
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-.01em', margin: '0 0 4px', color: '#1E2A2E' }}>
            Marca del organizador
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.5, color: '#5f6d70', margin: '0 0 26px' }}>
            Poca cosa, bien elegida. Esto se filtra a la landing, al panel y a la taquilla vía un solo token de color.
          </p>

          {/* IDENTIDAD VISUAL */}
          <SectionHead>Identidad visual</SectionHead>
          <div style={{ display: 'flex', gap: 16, marginBottom: 22 }}>
            <div style={{ flex: '0 0 auto' }}>
              <FieldLabel>Logo cuadrado</FieldLabel>
              <ImageSlot url={form.logo_url} onChange={(v) => patch('logo_url', v)} shape="rounded" size={96} placeholder="PNG 200×200" />
              <Hint>Mín 200×200 · PNG transparente</Hint>
            </div>
            <div style={{ flex: '1 1 auto' }}>
              <FieldLabel>Portada 16/9</FieldLabel>
              <ImageSlot url={form.cover_url} onChange={(v) => patch('cover_url', v)} shape="rect" aspect="16/9" placeholder="Hero de landing" />
              <Hint>Se usa en el hero público</Hint>
            </div>
          </div>

          {/* COLOR DE ACENTO */}
          <SectionHead>Color de acento</SectionHead>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <label style={{ position: 'relative', width: 52, height: 52, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(30,42,46,.16)', cursor: 'pointer', flex: '0 0 auto', background: 'var(--brand)' }}>
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(form.accent_color) ? form.accent_color : '#1E2A2E'}
                onChange={(e) => patch('accent_color', e.target.value.toUpperCase())}
                style={{ position: 'absolute', inset: -6, width: 64, height: 64, border: 'none', padding: 0, background: 'none', cursor: 'pointer' }}
                aria-label="Color de acento"
              />
            </label>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(30,42,46,.16)', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                <span style={{ padding: '0 4px 0 12px', fontSize: 14, color: '#9aa5a7', fontWeight: 600 }}>#</span>
                <input
                  value={form.accent_color.replace('#', '')}
                  onChange={(e) => patch('accent_color', '#' + e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6).toUpperCase())}
                  maxLength={6}
                  spellCheck={false}
                  style={{ flex: 1, border: 'none', padding: '11px 12px 11px 2px', fontSize: 14, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: '#1E2A2E', background: 'transparent', outline: 'none' }}
                />
              </div>
              <div style={{ fontSize: 11, color: '#9aa5a7', marginTop: 5 }}>
                Se aplica como <code style={{ fontFamily: 'ui-monospace, monospace' }}>var(--brand)</code> en todo
              </div>
            </div>
          </div>

          {/* Feedback contraste */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <ContrastPill label="Sobre blanco" swatch="#fff" ok={onWhitePass} />
            <ContrastPill label="Sobre negro" swatch="#0a0a0a" ok={onBlackPass} />
          </div>

          {'reason' in validation && (
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '11px 13px', borderRadius: 9, background: '#FEF3C7', border: '1px solid #FBBF24', marginBottom: 8 }}>
              <span style={{ fontSize: 14, lineHeight: 1.3 }}>⚠</span>
              <div style={{ fontSize: 12.5, lineHeight: 1.45, color: '#7a5a12', fontWeight: 500 }}>
                {validation.reason}
              </div>
            </div>
          )}

          <div style={{ fontSize: 11, color: '#9aa5a7', lineHeight: 1.5, marginBottom: 26 }}>
            El acento nunca colisiona con la semántica (verde/ámbar/rojo). Si falla contraste en blanco <em>y</em> negro, hay que cambiarlo.
          </div>

          {/* TEXTOS */}
          <SectionHead>Textos</SectionHead>

          <FieldWithCount
            label="Nombre"
            extra="para nav y hero"
            value={form.name}
            onChange={(v) => patch('name', v)}
            max={MAX_LONG}
          />

          <FieldWithCount
            label="Descripción"
            extra="markdown básico"
            value={form.description}
            onChange={(v) => patch('description', v)}
            max={MAX_DESC}
            multiline
          />

          <div style={{ display: 'flex', gap: 12, marginBottom: 30 }}>
            <SimpleField label="Web" extra="opc." value={form.website_url} onChange={(v) => patch('website_url', v)} placeholder="tu-web.com" />
            <SimpleField label="Instagram" extra="opc." value={form.instagram_url} onChange={(v) => patch('instagram_url', v)} placeholder="@usuario" />
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={!isColorOk || busy}
            style={{
              width: '100%', border: 'none', borderRadius: 11, padding: 14,
              fontSize: 14.5, fontWeight: 700,
              color: 'var(--brand-ink)', background: 'var(--brand)',
              cursor: (!isColorOk || busy) ? 'not-allowed' : 'pointer',
              opacity: (!isColorOk || busy) ? 0.5 : 1,
            }}
          >
            {busy ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </aside>

      {/* ═══ PREVIEW DERECHA ═══ */}
      <main style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid rgba(30,42,46,.1)', background: '#FBF7F0' }}>
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#7d8a8d' }}>
            Vista previa en vivo
          </span>
          <div style={{ display: 'inline-flex', background: '#ECE6DB', borderRadius: 10, padding: 3, gap: 2 }}>
            <WorldTab active={world === 'landing'} onClick={() => setWorld('landing')}>Landing</WorldTab>
            <WorldTab active={world === 'panel'} onClick={() => setWorld('panel')}>Panel</WorldTab>
            <WorldTab active={world === 'taquilla'} onClick={() => setWorld('taquilla')}>Taquilla</WorldTab>
          </div>
        </div>

        {/* Stage */}
        <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: 34, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', background: '#E9E4DA' }}>
          <div style={{ maxWidth: 760, width: '100%' }}>
            {world === 'landing' && (
              <LandingPreview name={form.name} short={shortName} monogram={monogram} description={form.description} coverUrl={form.cover_url} />
            )}
            {world === 'panel' && (
              <PanelPreview short={shortName} monogram={monogram} slug={entity.slug} />
            )}
            {world === 'taquilla' && (
              <TaquillaPreview short={shortName} monogram={monogram} slug={entity.slug} />
            )}
            <PreviewCaption>
              {world === 'landing' && <>Landing · <code>/o/{entity.slug}</code> — editorial dramático, <code>var(--brand)</code> mucho</>}
              {world === 'panel' && <>Panel · <code>/o/{entity.slug}/admin</code> — sobrio profesional, el acento solo acompaña</>}
              {world === 'taquilla' && <>Taquilla · <code>/o/{entity.slug}/taquilla</code> — dark utility, verde/rojo jamás se tocan</>}
            </PreviewCaption>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ═══════════════ Subcomponentes UI ═══════════════ */

function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: '#9aa5a7', margin: '0 0 14px' }}>
      {children}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#3a4d53', marginBottom: 7 }}>{children}</label>;
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, color: '#9aa5a7', marginTop: 6, lineHeight: 1.35 }}>{children}</div>;
}

function ContrastPill({ label, swatch, ok }: { label: string; swatch: string; ok: boolean }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', borderRadius: 9, background: '#fff', border: '1px solid rgba(30,42,46,.1)' }}>
      <span style={{ width: 20, height: 20, borderRadius: 5, background: swatch, border: '1px solid rgba(30,42,46,.18)', display: 'inline-block' }} />
      <div>
        <div style={{ fontSize: 10, color: '#9aa5a7', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: ok ? '#137a3e' : '#c23a2e' }}>
          {ok ? '✓ Suficiente' : '✗ Bajo contraste'}
        </div>
      </div>
    </div>
  );
}

function ImageSlot({ url, onChange, shape, size, aspect, placeholder }: {
  url: string; onChange: (v: string) => void;
  shape: 'rounded' | 'rect'; size?: number; aspect?: string; placeholder: string;
}) {
  const style: React.CSSProperties = {
    width: size ? size : '100%',
    height: size ? size : 'auto',
    aspectRatio: aspect,
    borderRadius: 14,
    overflow: 'hidden',
    border: '1px solid rgba(30,42,46,.14)',
    background: '#fff',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  return (
    <div>
      <div style={style}>
        {url ? (
          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 11, color: '#9aa5a7', textAlign: 'center', padding: 8 }}>{placeholder}</span>
        )}
      </div>
      <input
        value={url}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://…"
        style={{
          marginTop: 6, width: shape === 'rounded' ? (size || 96) : '100%',
          padding: '6px 8px', border: '1px solid rgba(30,42,46,.14)', borderRadius: 6,
          fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#3a4d53',
        }}
      />
    </div>
  );
}

function FieldWithCount({ label, extra, value, onChange, max, multiline }: {
  label: string; extra?: string; value: string; onChange: (v: string) => void; max: number; multiline?: boolean;
}) {
  const over = value.length > max;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#3a4d53' }}>
          {label} {extra && <span style={{ color: '#9aa5a7', fontWeight: 400 }}>· {extra}</span>}
        </label>
        <span style={{ fontSize: 11, color: over ? '#c23a2e' : '#9aa5a7', fontVariantNumeric: 'tabular-nums' }}>
          {value.length}/{max}
        </span>
      </div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          style={{ width: '100%', border: '1px solid rgba(30,42,46,.16)', borderRadius: 10, padding: '11px 12px', fontSize: 14, lineHeight: 1.5, color: '#1E2A2E', background: '#fff', resize: 'vertical', minHeight: 76, fontFamily: 'inherit', outline: 'none' }}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '100%', border: '1px solid rgba(30,42,46,.16)', borderRadius: 10, padding: '11px 12px', fontSize: 14, color: '#1E2A2E', background: '#fff', outline: 'none' }}
        />
      )}
    </div>
  );
}

function SimpleField({ label, extra, value, onChange, placeholder }: {
  label: string; extra?: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div style={{ flex: 1 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#3a4d53', marginBottom: 7 }}>
        {label} {extra && <span style={{ color: '#9aa5a7', fontWeight: 400 }}>· {extra}</span>}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', border: '1px solid rgba(30,42,46,.16)', borderRadius: 10, padding: '11px 12px', fontSize: 14, color: '#1E2A2E', background: '#fff', outline: 'none' }}
      />
    </div>
  );
}

function WorldTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: 'none', cursor: 'pointer', padding: '8px 15px', borderRadius: 8,
        fontSize: 13, fontWeight: 600,
        color: active ? '#1E2A2E' : '#7d8a8d',
        background: active ? '#fff' : 'transparent',
        boxShadow: active ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PreviewCaption({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12.5, color: '#7d8a8d' }}>
      {children}
    </div>
  );
}

/* ═══════════════ Previews de las 3 vistas ═══════════════ */

function LandingPreview({ name, short, monogram, description, coverUrl }: {
  name: string; short: string; monogram: string; description: string; coverUrl: string;
}) {
  return (
    <div style={{ borderRadius: 16, overflow: 'hidden', boxShadow: '0 30px 70px -30px rgba(30,42,46,.45)', background: '#0f1416' }}>
      <div style={{ position: 'relative', height: 430, background: '#0f1416' }}>
        {coverUrl && (
          <img src={coverUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,12,13,.15) 0%, rgba(10,12,13,.5) 55%, rgba(10,12,13,.92) 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 26px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--brand)', color: 'var(--brand-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>{monogram}</div>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{short || 'Nombre corto'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--brand)' }} />
            <span style={{ color: 'rgba(255,255,255,.72)', fontSize: 13, fontWeight: 500 }}>Eventos</span>
          </div>
        </div>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '0 34px 34px' }}>
          <div style={{ display: 'inline-block', fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--brand-ink)', background: 'var(--brand)', padding: '6px 12px', borderRadius: 6, marginBottom: 16 }}>
            Entradas abiertas
          </div>
          <h2 style={{ fontFamily: 'Archivo, sans-serif', fontWeight: 900, fontSize: 52, lineHeight: 0.98, letterSpacing: '-.02em', color: '#fff', margin: '0 0 14px', maxWidth: '16ch' }}>
            {name || 'Nombre largo del evento del organizador'}
          </h2>
          <p style={{ color: 'rgba(255,255,255,.82)', fontSize: 15.5, lineHeight: 1.55, maxWidth: '52ch', margin: '0 0 22px' }}>
            {description || 'Descripción breve del organizador — dos frases sobre qué hace y por qué vale la pena.'}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button style={{ border: 'none', cursor: 'default', padding: '14px 26px', borderRadius: 10, fontSize: 15, fontWeight: 700, color: 'var(--brand-ink)', background: 'var(--brand)' }}>
              Reservar entradas
            </button>
            <button style={{ border: '1px solid rgba(255,255,255,.3)', cursor: 'default', padding: '14px 22px', borderRadius: 10, fontSize: 15, fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,.06)' }}>
              Cartelera
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PanelPreview({ short, monogram, slug }: { short: string; monogram: string; slug: string }) {
  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', background: '#FBF7F0', boxShadow: '0 20px 50px -30px rgba(30,42,46,.4)', border: '1px solid rgba(30,42,46,.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 20px', background: '#fff', borderBottom: '1px solid rgba(30,42,46,.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--brand)', color: 'var(--brand-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>{monogram}</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2, color: '#1E2A2E' }}>{short || 'Nombre'}</div>
            <div style={{ fontSize: 12, color: '#7d8a8d' }}>Estás editando · <span style={{ fontWeight: 600, color: '#3a4d53' }}>Fiestas de la Virgen 2026</span></div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button style={{ display: 'flex', alignItems: 'center', gap: 7, border: '1px solid rgba(30,42,46,.16)', background: '#fff', cursor: 'default', padding: '9px 13px', borderRadius: 9, fontSize: 13, fontWeight: 600, color: '#1E2A2E' }}>
            🔗 Copiar enlace
          </button>
          <button style={{ border: '1px solid var(--brand)', background: 'var(--brand-soft)', cursor: 'default', padding: '9px 13px', borderRadius: 9, fontSize: 13, fontWeight: 600, color: 'var(--brand-strong)' }}>
            Salir a mi landing ↗
          </button>
        </div>
      </div>

      <div style={{ padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 18 }}>
          <MetricCard label="Vendidas" value="47" extra="/ 100" progress={0.47} />
          <MetricCard label="Ingresos" value="2.350€" trend="▲ 8% vs. semana pasada" />
          <MetricCard label="Últimas 24h" value="+12" note="reservas nuevas" />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid rgba(30,42,46,.14)', borderRadius: 9, padding: '9px 12px' }}>
            <span style={{ color: '#9aa5a7', fontSize: 13 }}>🔎</span>
            <span style={{ color: '#9aa5a7', fontSize: 13.5 }}>Buscar por nombre o email…</span>
          </div>
          <span style={{ fontSize: 12.5, color: '#7d8a8d', fontWeight: 500, whiteSpace: 'nowrap' }}>3 resultados</span>
          <button style={{ border: '1px solid rgba(30,42,46,.16)', background: '#fff', cursor: 'default', padding: '9px 13px', borderRadius: 9, fontSize: 13, fontWeight: 600, color: '#3a4d53' }}>
            Exportar CSV
          </button>
        </div>

        <div style={{ background: '#fff', border: '1px solid rgba(30,42,46,.1)', borderRadius: 11, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr .8fr 1fr', padding: '11px 16px', borderBottom: '1px solid rgba(30,42,46,.08)', fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: '#9aa5a7' }}>
            <div>Asistente</div><div>Email</div><div>Entradas</div><div>Estado</div>
          </div>
          <PanelRow name="María Ruiz" email="maria@example.com" qty="2" status="Confirmada" color="#137a3e" bg={withAlpha('#137a3e', 0.09)} />
          <PanelRow name="Antonio Pérez" email="antonio@example.com" qty="4" status="Confirmada" color="#137a3e" bg={withAlpha('#137a3e', 0.09)} />
          <PanelRow name="Lucía Gómez" email="lucia@example.com" qty="1" status="Pendiente" color="#7a5a12" bg="#FEF3C7" />
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, extra, progress, trend, note }: {
  label: string; value: string; extra?: string; progress?: number; trend?: string; note?: string;
}) {
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(30,42,46,.1)', borderRadius: 11, padding: '14px 16px' }}>
      <div style={{ fontSize: 12, color: '#7d8a8d', fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: '#1E2A2E', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        {extra && <span style={{ fontSize: 14, color: '#9aa5a7', fontWeight: 600 }}>{extra}</span>}
      </div>
      {progress != null && (
        <div style={{ height: 5, borderRadius: 3, background: '#ECE6DB', marginTop: 9, overflow: 'hidden' }}>
          <div style={{ width: `${progress * 100}%`, height: '100%', background: 'var(--brand)' }} />
        </div>
      )}
      {trend && <div style={{ fontSize: 12, color: '#137a3e', fontWeight: 600, marginTop: 9 }}>{trend}</div>}
      {note && <div style={{ fontSize: 12, color: '#7d8a8d', fontWeight: 500, marginTop: 9 }}>{note}</div>}
    </div>
  );
}

function PanelRow({ name, email, qty, status, color, bg }: {
  name: string; email: string; qty: string; status: string; color: string; bg: string;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr .8fr 1fr', alignItems: 'center', padding: '13px 16px', borderBottom: '1px solid rgba(30,42,46,.06)', fontSize: 13.5 }}>
      <div style={{ fontWeight: 600, color: '#1E2A2E' }}>{name}</div>
      <div style={{ color: '#7d8a8d' }}>{email}</div>
      <div style={{ color: '#3a4d53', fontVariantNumeric: 'tabular-nums' }}>{qty}</div>
      <div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, color, background: bg }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
          {status}
        </span>
      </div>
    </div>
  );
}

function TaquillaPreview({ short, monogram, slug: _slug }: { short: string; monogram: string; slug: string }) {
  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', background: '#0a0a0a', boxShadow: '0 24px 60px -28px rgba(0,0,0,.7)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--brand)', color: 'var(--brand-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12 }}>{monogram}</div>
          <span style={{ color: '#fff', fontWeight: 600, fontSize: 13.5 }}>Taquilla · {short || 'Nombre'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,.5)', fontSize: 12.5 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22C55E' }} />
          En puerta
        </div>
      </div>
      <div style={{ padding: '26px 22px 30px', display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 20, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,.4)', fontWeight: 700, marginBottom: 14 }}>
            Validación de entradas
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            <div style={{ background: 'rgba(34,197,94,.12)', border: '1px solid rgba(34,197,94,.4)', borderRadius: 11, padding: 14 }}>
              <div style={{ fontSize: 12, color: '#4ade80', fontWeight: 600, marginBottom: 4 }}>Válidas</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: '#22C55E', fontVariantNumeric: 'tabular-nums' }}>312</div>
            </div>
            <div style={{ background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.4)', borderRadius: 11, padding: 14 }}>
              <div style={{ fontSize: 12, color: '#f87171', fontWeight: 600, marginBottom: 4 }}>Rechazadas</div>
              <div style={{ fontSize: 30, fontWeight: 800, color: '#EF4444', fontVariantNumeric: 'tabular-nums' }}>4</div>
            </div>
          </div>
          <button style={{ width: '100%', border: 'none', cursor: 'default', padding: 16, borderRadius: 11, fontSize: 15, fontWeight: 700, color: 'var(--brand-ink)', background: 'var(--brand)' }}>
            Escanear siguiente QR
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ width: '100%', aspectRatio: 1, borderRadius: 14, background: '#111', border: '1px dashed rgba(255,255,255,.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <div style={{ width: '56%', aspectRatio: 1, borderRadius: 10, background: 'repeating-conic-gradient(#fff 0 25%, #0a0a0a 0 50%) 0 0/12px 12px', opacity: 0.9 }} />
            <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,.4)', fontFamily: 'ui-monospace, monospace' }}>
              enfoca el código
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#22C55E', fontSize: 13, fontWeight: 600 }}>
            <span style={{ fontSize: 15 }}>✓</span> Última: OK · Fila 12
          </div>
        </div>
      </div>
    </div>
  );
}

// Referencias implicitas para que el linter no marque unused
void readableTextOn;
